import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";

import { useDeleteRoleMutation } from "state/rtkQuery/access/accessMutations";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import { showToast } from "utils/reusableToast";

// Deleting a role is far more destructive than deleting a single role
// assignment (DeleteAssignmentDialog): it can affect every identity currently
// assigned this role. That higher blast radius earns a higher-friction
// confirmation than a plain confirm click — typing the exact role name plus a
// separate explicit acknowledgment checkbox, both required before Delete
// enables, so an admin can't misclick their way into it.
const DeleteRoleDialog = ({ role, onClose }) => {
  const [deleteRole, { isLoading, isError }] = useDeleteRoleMutation();
  const [confirmText, setConfirmText] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [pasteBlocked, setPasteBlocked] = useState(false);

  useEffect(() => {
    if (role) {
      setConfirmText("");
      setAcknowledged(false);
      setLocalError(null);
      setPasteBlocked(false);
    }
  }, [role]);

  const blockPaste = (e) => {
    e.preventDefault();
    setPasteBlocked(true);
  };

  if (!role) return null;

  const roleName = role.role_name || "";
  const canDelete = confirmText === roleName && acknowledged;

  const handleClose = () => {
    setLocalError(null);
    onClose();
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    setLocalError(null);
    try {
      await deleteRole(role.id).unwrap();
      showToast({ title: "Role deleted", description: `"${roleName}" was permanently deleted.`, type: "success" });
      handleClose();
    } catch (err) {
      setLocalError(extractApiErrorMessage(err, "Failed to delete the role. Please try again."));
    }
  };

  return (
    <Dialog open={!!role} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete role?</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <p style={{ marginTop: 0 }}>
            This permanently deletes <strong>{roleName}</strong> and cannot be undone. Anyone currently
            assigned this role will lose the access it grants.
          </p>
          <TextField
            label={`Type "${roleName}" to confirm`}
            size="small"
            fullWidth
            value={confirmText}
            onChange={(e) => {
              setConfirmText(e.target.value);
              setPasteBlocked(false);
            }}
            onPaste={blockPaste}
            onDrop={blockPaste}
            onContextMenu={(e) => e.preventDefault()}
            autoComplete="off"
            sx={{ mt: 1 }}
          />
          {pasteBlocked && (
            <p style={{ color: "#d32f2f", fontWeight: 700, fontSize: 12, margin: "4px 0 0" }}>
              Copy-pasting isn&apos;t allowed here — please type the role name.
            </p>
          )}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
            label="I understand this will remove this role from every identity that holds it."
          />
          {(localError || isError) && (
            <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>{localError}</Alert>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleDelete} disabled={isLoading || !canDelete} color="error" variant="contained">
          Delete role
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteRoleDialog.propTypes = {
  role: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default DeleteRoleDialog;
